import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useChatWebSocket } from '../../../contexts/ChatWebSocketContext';
import { useInitializeChatSessionMutation, useSendMessageMutation } from './useChatSupportMutations';
import { useChatMessagesQuery } from './useChatSupportQueries';
import { userInfoSchema, messageSchema, UserInfoFormData, MessageFormData } from '../validationSchemas';
import { ChatSession, ChatMessage } from '../../../services/chatSupportService';
import { chatWebSocketService } from '../../../services/chatWebSocketService';
import { convertApiMessagesToChatMessages, generateUserMessageId, formatMessageText } from '../utils/messageUtils';

export const useChatSupport = () => {
  const defaultRoomKey = process.env.REACT_APP_CHAT_ROOM_KEY || 'lottoreco_chat';

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isUserInfoSubmitted, setIsUserInfoSubmitted] = useState(false);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [showConnectionNotice, setShowConnectionNotice] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [shouldRefetchMessages, setShouldRefetchMessages] = useState(false);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Global WebSocket context
  const {
    connectionState: wsConnectionState,
    isConnected,
    messages,
    unreadCount,
    hasNewMessages,
    isTyping,
    addMessage,
    clearMessages,
    markMessagesAsRead,
    resetNotificationState,
    sendTyping,
    connect: connectWebSocket,
    setChatOpen,
    sessionCleared,
    resetSessionClearedFlag
  } = useChatWebSocket();

  // Form methods for user info
  // Infer input and validated output separately from the resolver (room_key is optional).
  const userInfoMethods = useForm({
    resolver: yupResolver(userInfoSchema),
    defaultValues: {
      customer_name: '',
      customer_email: '',
      room_key: defaultRoomKey,
    },
  });

  // Form methods for messages
  const messageMethods = useForm<MessageFormData>({
    resolver: yupResolver(messageSchema),
    defaultValues: {
      message: '',
    },
  });

  // Mutation hooks
  const initializeChatSessionMutation = useInitializeChatSessionMutation();
  const sendMessageMutation = useSendMessageMutation();

  // Query hook for fetching old messages
  const chatMessagesQuery = useChatMessagesQuery(
    currentSession?.session_id || null,
    (isUserInfoSubmitted && !!currentSession) || shouldRefetchMessages
  );

  // Update chat open state when isOpen changes and register close function
  useEffect(() => {
    setChatOpen(isOpen, () => {
      setIsOpen(false);
      setIsMinimized(false);
    });
  }, [isOpen, setChatOpen]);

  // Listen for session cleared flag and reset all local state
  useEffect(() => {
    if (sessionCleared) {
      console.log('🔌 Session cleared detected, resetting ChatSupport local state');
      
      // Reset all local state
      setCurrentSession(null);
      setIsUserInfoSubmitted(false);
      setIsInitializing(false);
      setIsOpen(false);
      setIsMinimized(false);
      setShowConnectionNotice(false);
      setShowScrollToBottom(false);
      setShouldRefetchMessages(false);
      setIsSubmittingMessage(false);
      
      // Reset form states
      userInfoMethods.reset();
      messageMethods.reset();
      
      // Clear any timeouts
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      
      console.log('🔌 ChatSupport local state reset complete');
    }
  }, [sessionCleared, userInfoMethods, messageMethods]);

  // Handle old messages when they are fetched
  useEffect(() => {
    if (chatMessagesQuery.isSuccess && chatMessagesQuery.data && chatMessagesQuery.data.data && currentSession) {
      console.log('🔍 Loading old messages:', chatMessagesQuery.data);
      
      // Only clear existing messages if we're refetching (not on initial load)
      if (shouldRefetchMessages) {
        clearMessages();
      }
      
      // Convert API messages to ChatMessage format
      const oldMessages = convertApiMessagesToChatMessages(
        chatMessagesQuery.data.data.messages || [],
        currentSession.session_id
      );
      
      // Add messages to global context with deduplication
      oldMessages.forEach(message => {
        // Check if message already exists to avoid duplicates
        const messageExists = messages.some(msg => 
          msg.id === message.id || 
          (msg.message === message.message && 
           msg.sender === message.sender && 
           msg.sessionId === message.sessionId)
        );
        
        if (!messageExists) {
          addMessage(message);
        }
      });
      
      console.log('🔍 Old messages loaded:', oldMessages.length, 'messages');
      
      // Reset refetch flag
      setShouldRefetchMessages(false);
      
      // WebSocket connection will be handled by separate useEffect
    } else if (chatMessagesQuery.isError && currentSession) {
      // If no old messages or error, add welcome message and connect WebSocket
      console.log('🔍 No old messages found, adding welcome message');
      
      const userName = localStorage.getItem('user_name') || 'User';
      const welcomeMessage: ChatMessage = {
        id: Date.now().toString(),
        sessionId: currentSession.session_id,
        message: `Hello ${userName}! How can we help you today?`,
        sender: 'support',
        timestamp: new Date().toISOString(),
        isRead: true
      };
      
      // Clear existing messages and add welcome message
      clearMessages();
      addMessage(welcomeMessage);
      
      // Reset refetch flag
      setShouldRefetchMessages(false);
      
      // WebSocket connection will be handled by separate useEffect
    }
  }, [chatMessagesQuery.isSuccess, chatMessagesQuery.isError, chatMessagesQuery.data, currentSession?.session_id, shouldRefetchMessages, clearMessages, addMessage]);

  // Separate useEffect for WebSocket connection to prevent infinite loops
  useEffect(() => {
    if (currentSession?.session_id && isUserInfoSubmitted && !isConnected) {
      const connectWebSocketForSession = async () => {
        console.log('🔌 Connecting WebSocket for session:', currentSession.session_id);
        try {
          const testResult = await chatWebSocketService.testConnection(currentSession.session_id);
          if (testResult.success) {
            await connectWebSocket(currentSession.session_id);
            console.log('🔌 WebSocket connected successfully');
          } else {
            console.warn('🔌 WebSocket connection test failed:', testResult.error);
            console.log('🔌 Continuing with REST API fallback');
          }
        } catch (error) {
          console.error('🔌 Failed to connect WebSocket:', error);
          console.log('🔌 Continuing with REST API fallback');
        }
      };

      // Connect after history fetch settles (success or error), including empty-history sessions
      if (chatMessagesQuery.isSuccess || chatMessagesQuery.isError) {
        connectWebSocketForSession();
      }
    }
  }, [currentSession?.session_id, isUserInfoSubmitted, chatMessagesQuery.isSuccess, chatMessagesQuery.isError, isConnected, connectWebSocket]);

  const handleToggleChat = async () => {
    if (!isOpen) {
      // Mark messages as read when opening chat
      markMessagesAsRead();
      
      // Check if we already have a session in localStorage
      const existingSessionId = localStorage.getItem('chat_session_id');
      const existingSessionData = localStorage.getItem('chat_session_data');
      
      if (existingSessionId && existingSessionData) {
        // Reuse existing session
        console.log('🔌 Found existing session, reusing:', existingSessionId);
        try {
          const sessionData = JSON.parse(existingSessionData);
          setCurrentSession(sessionData);
          setIsUserInfoSubmitted(true);
          setShowConnectionNotice(false);
          
          // Connect WebSocket if not already connected
          if (!isConnected) {
            await connectWebSocket(existingSessionId);
          }
          
          // If we have no messages in the global context, reload them from the API
          if (messages.length === 0) {
            console.log('🔌 No messages in context, triggering message reload');
            setShouldRefetchMessages(true);
          }
        } catch (error) {
          console.error('Failed to parse existing session data:', error);
          // Fallback to creating new session
          await createNewSession();
        }
      } else {
        // No existing session, create new one
        await createNewSession();
      }
    }
    
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };

  const createNewSession = async () => {
    // Reset session cleared flag for new session
    resetSessionClearedFlag();
    
    // Check if user data exists in localStorage
    const userName = localStorage.getItem('user_name');
    const userEmail = localStorage.getItem('email');
    
    if (userName && userEmail) {
      // User data exists, directly initialize chat session
      console.log('🔌 User data found in localStorage, initializing chat session directly');
      setIsInitializing(true);
      
      try {
        const userInfo = {
          customer_name: userName,
          customer_email: userEmail,
          room_key: defaultRoomKey
        };
        
        const response = await initializeChatSessionMutation.mutateAsync(userInfo);
        if (response.success && response.data) {
          setCurrentSession(response.data);
          setIsUserInfoSubmitted(true);
          setShowConnectionNotice(true);
        }
      } catch (error) {
        console.error('Failed to initialize chat session:', error);
      } finally {
        setIsInitializing(false);
      }
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleUserInfoSubmit = async (data: UserInfoFormData) => {
    try {
      // Reset session cleared flag for new session
      resetSessionClearedFlag();
      
      const response = await initializeChatSessionMutation.mutateAsync({
        ...data,
        room_key: data.room_key || defaultRoomKey
      });
      if (response.success && response.data) {
        setCurrentSession(response.data);
        setIsUserInfoSubmitted(true);
        setShowConnectionNotice(true);
      }
    } catch (error) {
      console.error('Failed to initialize chat session:', error);
    }
  };

  const handleSendMessage = async (data: MessageFormData) => {
    if (!currentSession || isSubmittingMessage) {
      console.log('🔌 Message submission blocked - no session or already submitting');
      return;
    }
    
    // Prevent multiple submissions
    setIsSubmittingMessage(true);
    
    try {
      console.log('🔌 Sending message via REST API');
      
      // Generate a unique message ID that will be consistent across REST and WebSocket
      const messageId = generateUserMessageId(currentSession.session_id);
      
      // Add user message immediately for better UX with consistent ID
      const userMessage: ChatMessage = {
        id: messageId,
        sessionId: currentSession.session_id,
        message: formatMessageText(data.message), // Apply formatting to user messages
        sender: 'user',
        timestamp: new Date().toISOString(),
        isRead: true
      };
      addMessage(userMessage);
      
      // Send message via REST API
      const response = await sendMessageMutation.mutateAsync({
        sessionId: currentSession.session_id,
        messageData: data
      });
      
      if (response.success && response.data) {
        // Only add support response if it's not already in messages (avoid duplicates)
        if (response.data!.supportResponse) {
          const supportMessage = response.data!.supportResponse!;
          // Check if this support message already exists to avoid duplicates
          const messageExists = messages.some(msg => 
            msg.id === supportMessage.id || 
            (msg.message === supportMessage.message && 
             msg.sender === supportMessage.sender && 
             Math.abs(new Date(msg.timestamp).getTime() - new Date(supportMessage.timestamp).getTime()) < 1000)
          );
          
          if (!messageExists) {
            addMessage(supportMessage);
          }
        }
      }
      
      // Reset form only after successful submission
      messageMethods.reset();
    } catch (error) {
      console.error('Failed to send message:', error);
      // If sending fails, we might want to remove the optimistically added message
      // But for now, we'll keep it as the user did type the message
    } finally {
      // Always reset the submission state
      setIsSubmittingMessage(false);
    }
  };

  const handleCloseChat = async () => {
    if (currentSession) {
      try {
        // Reset local state but keep session data in localStorage for reuse
        setCurrentSession(null);
        setIsUserInfoSubmitted(false);
        setShowConnectionNotice(false);
        userInfoMethods.reset();
        messageMethods.reset();
        
        // Don't clear messages when closing chat - they should persist for session reuse
        // Only reset notification state to clear unread indicators
        resetNotificationState();
      } catch (error) {
        console.error('Failed to close chat session:', error);
      }
    }
    setIsOpen(false);
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    setShowScrollToBottom(false);
  };

  // Handle scroll detection to show/hide scroll-to-bottom button
  const handleScroll = () => {
    setShowScrollToBottom(true);
  };

  // Handle typing indicator
  const handleTyping = (message: string) => {
    if (isConnected && message.length > 0) {
      sendTyping(true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        sendTyping(false);
      }, 1000);
    }
  };

  // Handle key press in message input
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Enter key without Shift - send message
      event.preventDefault();
      
      // Prevent submission if already submitting
      if (isSubmittingMessage) {
        console.log('🔌 Key press submission blocked - already submitting');
        return;
      }
      
      const messageValue = messageMethods.getValues('message');
      if (messageValue.trim()) {
        handleSendMessage({ message: messageValue });
      }
    }
    // Shift + Enter allows new line (default behavior)
  };

  return {
    // State
    isOpen,
    isMinimized,
    isUserInfoSubmitted,
    currentSession,
    isInitializing,
    showScrollToBottom,
    isSubmittingMessage,
    messages,
    unreadCount,
    hasNewMessages,
    isTyping,
    showConnectionNotice,
    connectionState: wsConnectionState,
    
    // Form methods
    userInfoMethods,
    messageMethods,
    
    // Mutations
    initializeChatSessionMutation,
    sendMessageMutation,
    
    // Handlers
    handleToggleChat,
    handleMinimize,
    handleUserInfoSubmit,
    handleSendMessage,
    handleCloseChat,
    handleScroll,
    scrollToBottom,
    handleKeyPress,
  };
};
